import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Briefcase,
  Code2,
  Cpu,
  GraduationCap,
  Layers,
  Network,
  PlayCircle,
  Target,
} from 'lucide-react';
import problemsData from '../data/problems.json';
import './Home.css';

const pillars = [
  {
    icon: Code2,
    eyebrow: 'Guided Practice',
    title: 'Build understanding one circuit at a time.',
    copy: 'Solve focused quantum problems, test circuits in the browser, and use contextual hints when you need a nudge.',
    points: ['Structured problem paths', 'Immediate test feedback', 'Context-aware hints'],
  },
  {
    icon: Target,
    eyebrow: 'Interview Mastery',
    title: 'Practice like the interview is tomorrow.',
    copy: 'Follow structured coding reps, sharpen your problem-solving rhythm, and prepare for company-style technical screens with confidence.',
    points: ['Structured problem ladder', 'Mock interview flow', 'Role-focused prep'],
  },
  {
    icon: Cpu,
    eyebrow: 'Quantum Future',
    title: 'Build where the next wave begins.',
    copy: 'Quantum computing needs builders now. Learn the fundamentals, experiment in the simulator, and contribute while the field is still being shaped.',
    points: ['Hands-on quantum circuits', 'Simulator experiments', 'Builder-first learning'],
  },
];

const proofStats = [
  { value: `${Object.keys(problemsData).length}+`, label: 'quantum coding challenges' },
  { value: '3', label: 'growth paths in one platform' },
  { value: 'Live', label: 'browser quantum simulator' },
  { value: 'Fast', label: 'practice-to-project feedback loop' },
];

const learningFlow = [
  {
    icon: Code2,
    title: 'Solve',
    copy: 'Work through practical quantum and coding problems with clear, immediate feedback.',
  },
  {
    icon: Network,
    title: 'Build',
    copy: 'Turn circuit practice into working experiments and portfolio-ready quantum projects.',
  },
  {
    icon: Briefcase,
    title: 'Interview',
    copy: 'Use structured reps and mock-ready prompts to prepare for technical conversations.',
  },
];

const Home = ({ user }) => {
  const openAuthModal = () => {
    window.dispatchEvent(new CustomEvent('showAuthModal'));
  };

  const primaryCta = (to, label) =>
    user ? (
      <Link to={to} className="home-btn home-btn--primary">
        <span>{label}</span>
        <ArrowRight size={18} />
      </Link>
    ) : (
      <button type="button" className="home-btn home-btn--primary" onClick={openAuthModal}>
        <span>{label}</span>
        <ArrowRight size={18} />
      </button>
    );

  return (
    <div className="home-page">
      <section className="home-hero" aria-labelledby="home-hero-title">
        <div className="home-hero__copy">
          <h1 id="home-hero-title" className="home-hero__title">
            Start Building the Future
          </h1>

          <div className="home-hero__actions" aria-label="Homepage actions">
            {primaryCta('/problems', 'Start Solving')}
            <button type="button" className="home-btn home-btn--secondary" onClick={openAuthModal}>
              Join Free
            </button>
          </div>

          <p className="home-hero__microcopy">
            For beginners and ambitious developers who want to get hands-on with
            quantum computing and master technical interviews before the next wave arrives.
          </p>

        </div>

        <figure className="home-product-preview home-product-preview--hero">
          <div className="home-product-window">
            <div className="home-product-window__chrome" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
            <div className="home-product-stage">
              <img
                className="home-product-stage__statement"
                src="/home/hero-statement.png"
                alt="QubitCompile problem statement and expected output"
              />
              <img
                className="home-product-stage__simulator"
                src="/home/hero-simulator.png"
                alt="QubitCompile visual quantum circuit simulator"
              />
            </div>
          </div>
        </figure>
      </section>

      <section className="home-proof" aria-label="Platform proof points">
        {proofStats.map((stat) => (
          <div className="home-proof__item" key={stat.label}>
            <strong>{stat.value}</strong>
            <span>{stat.label}</span>
          </div>
        ))}
      </section>

      <section className="home-section home-pillars" aria-labelledby="home-pillars-title">
        <div className="home-section__header">
          <span className="home-section__eyebrow">Why join now</span>
          <h2 id="home-pillars-title">One platform for momentum, mastery, and impact.</h2>
          <p>
            Inspired by the best developer practice platforms, QubitCompile turns learning
            into visible progress and gives quantum builders a place to start today.
          </p>
        </div>

        <div className="home-pillars-grid">
          {pillars.map((pillar) => {
            const Icon = pillar.icon;
            return (
              <article className="home-pillar-card" key={pillar.eyebrow}>
                <div className="home-pillar-card__icon">
                  <Icon size={24} />
                </div>
                <span className="home-pillar-card__eyebrow">{pillar.eyebrow}</span>
                <h3>{pillar.title}</h3>
                <p>{pillar.copy}</p>
                <ul>
                  {pillar.points.map((point) => (
                    <li key={point}>
                      <ArrowRight size={14} />
                      <span>{point}</span>
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </section>

      <section className="home-section home-section--split" aria-labelledby="home-flow-title">
        <div className="home-section__header home-section__header--left">
          <span className="home-section__eyebrow">Growth system</span>
          <h2 id="home-flow-title">A clear path from first circuit to standout portfolio.</h2>
          {user ? (
            <Link to="/simulator" className="home-text-link">
              Explore the quantum simulator
              <ArrowRight size={16} />
            </Link>
          ) : (
            <button type="button" className="home-text-link home-text-link--button" onClick={openAuthModal}>
              Explore the quantum simulator
              <ArrowRight size={16} />
            </button>
          )}
        </div>

        <div className="home-flow-card">
          {learningFlow.map((item, index) => {
            const Icon = item.icon;
            return (
              <div className="home-flow-step" key={item.title}>
                <div className="home-flow-step__index">{String(index + 1).padStart(2, '0')}</div>
                <div className="home-flow-step__icon">
                  <Icon size={20} />
                </div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.copy}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="home-section home-lab" aria-labelledby="home-lab-title">
        <div className="home-section__header">
          <span className="home-section__eyebrow">Interactive workspace</span>
          <h2 id="home-lab-title">Practice in an environment that feels alive.</h2>
          <p>
            See the real Python editor, contextual hint coach, and visual circuit
            simulator learners use inside QubitCompile.
          </p>
        </div>

        <div className="home-screenshot-grid">
          <figure className="home-screenshot-card home-screenshot-card--editor">
            <img
              src="/home/code-editor.png"
              alt="QubitCompile Python code editor with a Qiskit circuit and Run Code"
              loading="lazy"
            />
            <figcaption>
              <strong>Python code editor</strong>
              <span>Write Qiskit circuits and run them in the browser.</span>
            </figcaption>
          </figure>

          <figure className="home-screenshot-card home-screenshot-card--hint">
            <img
              src="/home/hint-coach.png"
              alt="QubitCompile hint coach overlay giving a nudge over the visual simulator"
              loading="lazy"
            />
            <figcaption>
              <strong>Contextual hint coach</strong>
              <span>Get a useful nudge without revealing the solution.</span>
            </figcaption>
          </figure>

          <figure className="home-screenshot-card home-screenshot-card--simulator">
            <img
              src="/home/quantum-sandbox.png"
              alt="QubitCompile visual quantum circuit sandbox with gate palette and circuit wire"
              loading="lazy"
            />
            <figcaption>
              <strong>Visual quantum sandbox</strong>
              <span>Build circuits and inspect the live visual simulator in the browser.</span>
            </figcaption>
          </figure>
        </div>
      </section>

      <section className="home-testimonials" aria-labelledby="home-proof-title">
        <div className="home-section__header">
          <span className="home-section__eyebrow">Social proof</span>
          <h2 id="home-proof-title">Designed for builders who want a real edge.</h2>
        </div>

        <div className="home-testimonial-grid">
          <article className="home-testimonial-card">
            <GraduationCap size={22} />
            <p>
              "The best prep platforms show you exactly what to do next. QubitCompile
              brings that clarity to quantum learning and technical growth."
            </p>
            <strong>Student builder track</strong>
          </article>
          <article className="home-testimonial-card">
            <Layers size={22} />
            <p>
              "Practice, simulator work, and guided feedback belong together. That is
              how learners build real understanding."
            </p>
            <strong>Connected learning workflow</strong>
          </article>
          <article className="home-testimonial-card">
            <PlayCircle size={22} />
            <p>
              "Quantum is early, technical, and full of opportunity. The winners will be
              the people experimenting before it feels obvious."
            </p>
            <strong>Future-focused practice</strong>
          </article>
        </div>
      </section>

      <section className="home-cta-band" aria-labelledby="home-cta-title">
        <div>
          <span className="home-section__eyebrow">Your next rep starts here</span>
          <h2 id="home-cta-title">Join the builders preparing for what comes next.</h2>
          <p>Practice the fundamentals, build working circuits, and develop fluency in quantum computing.</p>
        </div>
        {primaryCta('/problems', 'Start Building')}
      </section>
    </div>
  );
};

export default Home;
